import React, { useRef, Suspense, useState, useMemo } from "react";
import { useObjectStore } from "../zustand/objects";
import { useShallow } from "zustand/react/shallow";
import PreviewSplash from "./PreviewSplash";
import Cursor from "./Cursor";
import PlayerFigure from "./PlayerFigure";
import { ChangePreviewScene } from "./ChangePreviewScene";
import { CaptureHeadset, CaptureCamera } from "./CaptureHeadset";
import { PreviewObjectsMapped, VRPreviewObjectsMapped } from "./PreviewObjectsMapped";
import { DesktopFloorTeleport, VRTeleportFloorGrid, ChangeSceneDesktop } from "./TeleportFloorGrid";
import "../dot.css";
import {
  VIEWPORT_SHADOW_MAP,
  getViewportSceneTheme,
  getViewportCappedDpr,
} from "../utils/viewportCanvasDefaults";

import { Button, Text, Group, Modal, Stack } from '@mantine/core';
import {
  editorModalShell,
  editorModalStrongTitleStyles,
  editorModalPrimaryButtonStyles,
  MBX_DARK_BACKGROUND_FILL,
  previewCanvasFrameStyle,
  previewCanvasWebGlStyle,
} from "../styleFile";

import { Canvas, useThree } from "@react-three/fiber";
import { PointerLockControls } from "@react-three/drei";
import { AtmosphereSky } from "./AtmosphereSky";
import { AnimatedSceneBackground } from "./AnimatedSceneBackground";
import { ViewportSunLights } from "./ViewportSunLights";
import { AnimatedFloorMaterial } from "./AnimatedFloorMaterial";
import { XR, Controllers, Hands, TeleportationPlane } from "@react-three/xr";
import { XrInvalidateWhilePresenting } from "./XrInvalidateWhilePresenting";
import { PreviewVRSessionControls } from "./PreviewVRSessionControls";
import { PreviewVRSessionSync } from "./PreviewVRSessionSync";
import PreviewPresenceLighting from "./PreviewPresenceLighting";
import { PreviewR3fInvalidateOnShapeHover } from "./PreviewR3fInvalidateOnShapeHover";

const PreviewViewport = ({ presence, projectKey, artboards, guestMode }) => {

  const [previewMode, setPreviewMode] = useState('desktop')
  const [modeSelectVisible, setModeSelectVisible] = useState(true)
  const [previewCanvasEl, setPreviewCanvasEl] = useState(null)

  const canvasRef = useRef();
  const vRCanvasRef = useRef();
  const floorRef = useRef();


  const { previewCameraPosition, floorColour, objectsAreLoaded, currentObjectArtboard, viewportThemeOverride } =
    useObjectStore(
      useShallow((s) => ({
        previewCameraPosition: s.previewCameraPosition,
        floorColour: s.floorColour,
        objectsAreLoaded: s.objectsAreLoaded,
        currentObjectArtboard: s.currentObjectArtboard,
        viewportThemeOverride: s.viewportThemeOverride,
      }))
    );

// - - - - cursors start

  const COLORS = [
    "#E57373",
    "#9575CD",
    "#4FC3F7",
    "#81C784",
    "#FFF176",
    "#FF8A65",
    "#F06292",
    "#7986CB",
  ];

  // - - - -  cursors end


  const controlsRef = useRef();

  const clearDestinationHover = () => {
    useObjectStore.getState().shapeHoverChange(false);
  };

  const handleClick = (val) => {
    setModeSelectVisible(false)
    setPreviewMode(val)
  }

  const cappedDpr = getViewportCappedDpr();
  const sceneTheme = getViewportSceneTheme(viewportThemeOverride);
  const povCanvasBackground = viewportThemeOverride === "dark" ? MBX_DARK_BACKGROUND_FILL : sceneTheme.background;

  /** Mantine modal portaled styles + var() fallbacks don’t track viewport theme reliably — mirror editor modal hex tokens. */
  const welcomeModalChrome = useMemo(() => {
    const dark = viewportThemeOverride === "dark";
    return dark
      ? {
          standardFill: "#2f2f2f",
          headerTitleText: "#e2e8f0",
          mutedBodyText: "#94a3b8",
          border: "#475569",
        }
      : {
          standardFill: "#ffffff",
          headerTitleText: "#111827",
          mutedBodyText: "#868e96",
          border: "#e5e7eb",
        };
  }, [viewportThemeOverride]);

  if (previewMode === "desktop") {
    return <>
      <div style={{ ...previewCanvasFrameStyle, backgroundColor: sceneTheme.mode === "dark" ? MBX_DARK_BACKGROUND_FILL : "#FFFFFF" }}>
      <Canvas
        ref={canvasRef}
        onCreated={({ gl, invalidate }) => {
          setPreviewCanvasEl(gl.domElement);
          invalidate();
        }}
        onPointerMissed={clearDestinationHover}
        style={previewCanvasWebGlStyle}
        camera={{
          position: [previewCameraPosition[0], 1.75, previewCameraPosition[2]],
          fov: 55
        }}
        dpr={cappedDpr}
        frameloop="demand"
        shadows
        gl={{ preserveDrawingBuffer: true }}
      >
        <PreviewR3fInvalidateOnShapeHover />
        <CaptureCamera projectKey={projectKey} currentObjectArtboard={currentObjectArtboard} />
        <AnimatedSceneBackground color={povCanvasBackground} />
        <AtmosphereSky {...sceneTheme.skyAtmosphere} />
        <ViewportSunLights
          ambientIntensity={sceneTheme.ambientIntensity}
          directionalIntensity={sceneTheme.directionalIntensity}
          directionalColor={sceneTheme.directionalColor}
          shadowMapSize={VIEWPORT_SHADOW_MAP}
        />
        <PreviewPresenceLighting
          intensity={sceneTheme.previewPresenceDirectionalIntensity}
          color={sceneTheme.previewPresenceDirectionalColor}
        />
        <PointerLockControls unlock={() => console.log("disconnect")} selector={"#splashy"} ref={controlsRef} />
        {objectsAreLoaded && <PreviewObjectsMapped projectKey={projectKey} guestMode={guestMode} artboards={artboards} />}
        <mesh
          receiveShadow
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, 0, 0]}
          ref={floorRef}
          userData={{ previewTeleportFloor: true }}
        >
          <planeGeometry attach="geometry" args={[50, 50]} />
          <AnimatedFloorMaterial
            color={floorColour}
            opacity={1}
          />
        </mesh>
        <DesktopFloorTeleport />
        <ChangeSceneDesktop />
        <CursorsAndHeadsets presence={presence} currentObjectArtboard={currentObjectArtboard} COLORS={COLORS} projectKey={projectKey}/>
        
      </Canvas>
      </div>
      {modeSelectVisible && (
        <Modal
          opened={modeSelectVisible}
          onClose={() => {}}
          closeOnEscape={false}
          withCloseButton={false}
          closeOnClickOutside={false}
          title="Welcome to preview mode"
          radius={editorModalShell.radius}
          centered={editorModalShell.centered}
          overlayProps={{
            ...editorModalShell.overlayProps,
            style: { pointerEvents: "none" },
          }}
          styles={{
            ...editorModalStrongTitleStyles,
            title: {
              ...editorModalStrongTitleStyles.title,
              color: welcomeModalChrome.headerTitleText,
            },
            inner: { pointerEvents: "none" },
            content: {
              ...editorModalShell.styles?.content,
              backgroundColor: welcomeModalChrome.standardFill,
              border: `1px solid ${welcomeModalChrome.border}`,
              pointerEvents: "auto",
            },
            header: {
              ...editorModalShell.styles?.header,
              backgroundColor: welcomeModalChrome.standardFill,
              borderBottom: `1px solid ${welcomeModalChrome.border}`,
            },
            body: {
              padding: "20px 24px 24px",
              pointerEvents: "auto",
              backgroundColor: welcomeModalChrome.standardFill,
              color: welcomeModalChrome.mutedBodyText,
            },
          }}
          size="md"
          zIndex={400}
        >
          <Stack gap="md">
            <Text size="sm" style={{ color: welcomeModalChrome.mutedBodyText }}>
              What device are you using?{" "}
              <Text component="span" fs="italic" size="sm" style={{ color: welcomeModalChrome.mutedBodyText }}>
                You can refresh this page to change your selection.
              </Text>
            </Text>
            <Group gap="sm" wrap="wrap">
              <Button styles={editorModalPrimaryButtonStyles} size="md" onClick={() => handleClick("desktop")}>
                Desktop
              </Button>
              <Button styles={editorModalPrimaryButtonStyles} size="md" onClick={() => handleClick("VR")}>
                VR headset
              </Button>
            </Group>
            <Stack gap={6}>
              <Text fw={600} size="sm" style={{ color: welcomeModalChrome.headerTitleText }}>
                VR teleporting
              </Text>
              <Text size="sm" style={{ color: welcomeModalChrome.mutedBodyText }}>
                Point your controller at the floor and select to teleport. Hold the select button to show scene-change controls.
              </Text>
            </Stack>
            <Stack gap={6}>
              <Text fw={600} size="sm" style={{ color: welcomeModalChrome.headerTitleText }}>
                Desktop / laptop
              </Text>
              <Text size="sm" style={{ color: welcomeModalChrome.mutedBodyText }}>
                Point at the floor to see a ring where you would land, then click to teleport (blocked if something is in the way). Use the artboard controls in the header or click interactive objects to change scenes.
              </Text>
            </Stack>
            <Text size="xs" style={{ color: welcomeModalChrome.mutedBodyText }}>
              You can also change scenes by selecting objects set as interactive.
            </Text>
          </Stack>
        </Modal>
      )}
      <PreviewSplash canvasElement={previewCanvasEl} />
    </>

  }
  else if (previewMode === "VR") {
    return (
      <PreviewVRModeShell
        vRCanvasRef={vRCanvasRef}
        floorColour={floorColour}
        currentObjectArtboard={currentObjectArtboard}
        presence={presence}
        COLORS={COLORS}
        projectKey={projectKey}
        guestMode={guestMode}
        cappedDpr={cappedDpr}
        sceneTheme={sceneTheme}
        povCanvasBackground={povCanvasBackground}
      />
    );
  }


};

const CursorsAndHeadsets = ({ presence, currentObjectArtboard, COLORS, projectKey }) => {
  const localSocketId = useObjectStore((s) => s.realtimeClientSocketId);
  const rowForProject = (row) =>
    row && (projectKey == null || String(row.projectKey) === String(projectKey));

  return (
      <>
      {/* CURSORS */}
        <Suspense fallback={null}>
          {Object.keys(presence).length > 0 && Object.keys(presence).map((mapped) =>
            rowForProject(presence[mapped]) && presence[mapped].update.cursor && presence[mapped].update.cursor.page === 'editor' && String(presence[mapped].update.cursor.artboard) === String(currentObjectArtboard) && <Cursor key={mapped} rotation={presence[mapped].update.cursor.rotation} position={presence[mapped].update.cursor.position} color={COLORS[mapped.match(/\d+/)[0] % COLORS.length]} mouse={presence[mapped].update.cursor.mouse} />
          )}</Suspense>
          {/* DESKTOP HEADSETS */}
        <Suspense fallback={null}>
          {Object.keys(presence).length > 0 && Object.keys(presence).map((mapped) =>
            rowForProject(presence[mapped]) && presence[mapped].update.preview && presence[mapped].update.preview.page === 'preview' && String(presence[mapped].update.preview.artboard) === String(currentObjectArtboard) && mapped !== localSocketId && <PlayerFigure key={mapped} rotation={presence[mapped].update.preview.rotation} position={presence[mapped].update.preview.position} color={COLORS[mapped.match(/\d+/)[0] % COLORS.length]} device={presence[mapped].update.preview.device} />
          )}</Suspense>
      </>
    )
}

/** VR preview canvas + DOM enter/exit control; keeps WebXR presentation state in the same scope as its uses. */
function PreviewVRModeShell({
  vRCanvasRef,
  floorColour,
  currentObjectArtboard,
  presence,
  COLORS,
  projectKey,
  guestMode,
  cappedDpr,
  sceneTheme,
  povCanvasBackground,
}) {
  const [vrPresenting, setVrPresenting] = useState(false);
  return (
    <Suspense fallback={<div></div>}>
      <div style={{ ...previewCanvasFrameStyle, backgroundColor: sceneTheme.mode === "dark" ? MBX_DARK_BACKGROUND_FILL : "#FFFFFF" }}>
      <Canvas
        ref={vRCanvasRef}
        camera={{
          fov: 90,
        }}
        style={previewCanvasWebGlStyle}
        dpr={cappedDpr}
        frameloop="demand"
        shadows
        onCreated={(state) => state.invalidate()}
        onPointerMissed={() => useObjectStore.getState().shapeHoverChange(false)}
      >
        <PreviewR3fInvalidateOnShapeHover />
        <XR>
          <PreviewVRSessionSync onPresentingChange={setVrPresenting} />
          <XrInvalidateWhilePresenting />
          <CaptureHeadset currentObjectArtboard={currentObjectArtboard} />
          <TeleportationPlane
            leftHand={false}
            rightHand={true}
            maxDistance={10}
            size={0.5}
          />

          <AnimatedSceneBackground color={povCanvasBackground} />
          <AtmosphereSky {...sceneTheme.skyAtmosphere} />
          <ViewportSunLights
            ambientIntensity={sceneTheme.ambientIntensity}
            directionalIntensity={sceneTheme.directionalIntensity}
            directionalColor={sceneTheme.directionalColor}
            shadowMapSize={VIEWPORT_SHADOW_MAP}
          />
          <PreviewPresenceLighting
            intensity={sceneTheme.previewPresenceDirectionalIntensity}
            color={sceneTheme.previewPresenceDirectionalColor}
          />
          <Controllers rayMaterial={{ color: "blue" }} />
          <Hands />
          <group>
            <VRPreviewObjectsMapped projectKey={projectKey} guestMode={guestMode} />
          </group>
          <CursorsAndHeadsets
            presence={presence}
            currentObjectArtboard={currentObjectArtboard}
            COLORS={COLORS}
            projectKey={projectKey}
          />
          <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
            <planeGeometry attach="geometry" args={[50, 50]} />
            <AnimatedFloorMaterial
              color={floorColour}
              opacity={1}
            />
          </mesh>
          <VRTeleportFloorGrid />
        </XR>
      </Canvas>
      </div>
      <PreviewVRSessionControls
        isPresenting={vrPresenting}
        sessionInit={{ optionalFeatures: ["hand-tracking"] }}
      />
    </Suspense>
  );
}

export default PreviewViewport; 